import { cloneElement, useMemo, useRef } from 'react';
import type { JSXElementConstructor, ReactElement, ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import type { DialogProps } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import CardHeader from '@mui/material/CardHeader';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { ModalActionButton } from 'interfaces/components';
import { getOppositeColor } from 'helpers/utils';

const BlueCard = styled(Card)(() => ({
  background: 'rgba(247, 253, 255, 0.63)',
  borderRadius: '4px'
}));

/**
 * Componente CustomFormChangeStatusModal
 *
 * Modal especializado para cambiar el estado de tickets u objetos.
 *
 * @component
 * @param {object} props - Props del componente.
 * @param {DialogProps} props.dialogProps - Props del componente Dialog de MUI.
 * @param {ModalActionButton[]} [props.actions] - Acciones personalizadas para el modal.
 * @param {string} [props.originStatus] - Estado de origen.
 * @param {object} [props.destinyStatus] - Estado de destino.
 * @param {ReactNode} [props.headerContent] - Contenido adicional en el encabezado.
 * @param {ReactNode} [props.bodyContent] - Contenido principal del modal.
 * @param {boolean} [props.isMutating] - Indica si hay una acción en curso.
 * @param {ReactElement} props.customForm - Formulario personalizado.
 * @param {() => void} [props.onClickViewDetail] - Callback para ver detalles.
 * @param {() => void} [props.onCancelClickButton] - Callback al cancelar.
 * @param {string[]} props.ticketIds - IDs de los tickets afectados.
 * @param {ReactNode} [props.children] - Elementos hijos.
 * @param {boolean} [props.hideAcceptButton] - Oculta el botón de aceptar.
 * @param {boolean} [props.hideCancelButton] - Oculta el botón de cancelar.
 * @param {string} [props.acceptButtonText] - Texto del botón de aceptar.
 * @param {string} [props.maxWidth] - Ancho máximo del modal.
 * @example
 * <CustomFormChangeStatusModal dialogProps={{ open: true, onClose: () => {} }} ticketIds={['1']} customForm={<form />} />
 */
type Props = {
  dialogProps: DialogProps;
  actions?: ModalActionButton[];
  originStatus?: string;
  destinyStatus?: { id: number; name?: string };
  headerContent?: ReactNode;
  bodyContent?: ReactNode;
  isMutating?: boolean;
  customForm: ReactElement<unknown, string | JSXElementConstructor<unknown>>;
  onClickViewDetail?: () => void;
  onCancelClickButton?: () => void;
  ticketIds: string[];
  children?: ReactNode;
  hideAcceptButton?: boolean;
  hideCancelButton?: boolean;
  acceptButtonText?: string;
  maxWidth?: string;
};

function CustomFormChangeStatusModal({
  children,
  dialogProps,
  ticketIds,
  originStatus,
  destinyStatus,
  headerContent,
  bodyContent,
  isMutating = false,
  customForm,
  onClickViewDetail,
  onCancelClickButton,
  actions,
  acceptButtonText,
  hideAcceptButton = false,
  hideCancelButton = false,
  maxWidth = null
}: Props) {
  const hasManyTickets = useMemo(() => ticketIds.length > 1, [ticketIds]);

  const formRef = useRef<HTMLFormElement>(null);

  let addedForm: ReactElement | undefined;

  if (customForm) {
    addedForm = cloneElement(customForm, {
      // onSubmit: onSubmitClickButton,
      ref: formRef
    });
  }

  const { t } = useTranslation('global');

  const handleSubmitEvent = () => {
    if (formRef.current)
      formRef.current?.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      );
  };

  const renderButtons: ModalActionButton[] = useMemo(
    () => [
      ...(!hideCancelButton
        ? [
            {
              backgroundColor: '#f7fdff',
              text: t('common.commonActions.invalidate'),
              variant: 'outlined',
              opacity: 0.7,
              handle: onCancelClickButton
            }
          ]
        : []),
      ...(actions || []),
      ...(!hideAcceptButton
        ? [
            {
              backgroundColor: '#1D6FCC',
              text: acceptButtonText || t('common.commonActions.accept'),
              variant: 'contained',
              form: addedForm.props.id,
              type: 'submit',
              handle: handleSubmitEvent
            }
          ]
        : [])
    ],
    [onCancelClickButton, actions]
  );

  return (
    <Dialog
      {...dialogProps}
      onClose={isMutating ? undefined : onCancelClickButton}
      scroll="body"
      sx={{
        zIndex: '1300',
        '& .MuiDialog-paper': {
          maxWidth: maxWidth || 'sm'
        }
      }}
      fullWidth
    >
      <DialogContent sx={{ padding: '10px 24px' }}>
        <DialogTitle sx={{ m: 0, p: 1 }}>
          {onCancelClickButton ? (
            <IconButton
              aria-label="close"
              disabled={isMutating}
              onClick={onCancelClickButton}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
        <BlueCard>
          <CardHeader
            subheader={
              <Typography
                color="#063468"
                fontSize="14px"
                fontWeight="500"
                textAlign="center"
              >
                {dialogProps?.title || t('ticket.changeStatus.updatingStatus')}
              </Typography>
            }
          />
          <CardHeader
            title={
              <Typography
                color="#063468"
                fontWeight="400"
                sx={{
                  fontSize: {
                    xs: '16px',
                    sm: '20px'
                  }
                }}
                textAlign="center"
              >
                {headerContent ||
                  `${
                    originStatus
                      ? t('ticket.changeStatus.updateTicketHeader', {
                          origin: originStatus || '',
                          destiny: destinyStatus?.name || ''
                        })
                      : t('ticket.changeStatus.updateToState', {
                          origin: destinyStatus?.name || ''
                        })
                  } ${
                    hasManyTickets
                      ? t('ticket.changeStatus.theTickets')
                      : t('ticket.changeStatus.theTicket')
                  } ${ticketIds.join(', ')}?`}
              </Typography>
            }
          />
          {onClickViewDetail && (
            <Typography
              color="#0E5AAF"
              fontSize="12px"
              fontWeight="500"
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              textAlign="center"
            >
              {t('ticket.changeStatus.viewTicketDetail')}
            </Typography>
          )}
          <CardContent sx={{ padding: 2 }}>
            {bodyContent}
            {addedForm}
            {children && children}
          </CardContent>
        </BlueCard>
      </DialogContent>
      <DialogActions sx={{ paddingX: '16px' }}>
        <Stack direction="row" justifyContent="space-between" width="100%">
          {renderButtons?.map(
            ({
              backgroundColor,
              opacity,
              text,
              variant,
              handle,
              form,
              type,
              disabled
            }) => (
              <Button
                disabled={isMutating || disabled}
                form={form}
                key={text}
                onClick={handle}
                size="large"
                sx={{
                  minWidth: '85px',
                  maxWidth: '150px',
                  width: '100%',
                  opacity: opacity || 1,
                  backgroundColor,
                  color: getOppositeColor(backgroundColor),
                  marginX: 1
                }}
                type={type}
                variant={variant || 'contained'}
              >
                {text}
              </Button>
            )
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default CustomFormChangeStatusModal;
